/**
 * Panel for exercising the vcs-service GitHub App install/uninstall flow end-to-end.
 *
 * Gated by the `VCS_SERVICE_GITHUB_APP` feature flag, it renders wherever that flag is enabled, regardless of the env
 */
import { useEffect } from 'react';

import { VCS_SERVICE_GITHUB_APP } from '../../../../shared/featureFlags/flagNames';
import { VCS_APP_ROLE } from '../../../../shared/vcs/githubApp/apps';
import { useAddToast } from '../../../contexts/ToastContext';
import { isFeatureEnabled } from '../../../helpers/features/featureFlags';
import { useGitHubAppInstallation } from '../../../hooks/api/vcs/githubApp/useGitHubAppInstallation';
import { useStartGitHubAppInstall } from '../../../hooks/api/vcs/githubApp/useStartGitHubAppInstall';
import { useUninstallGitHubApp } from '../../../hooks/api/vcs/githubApp/useUninstallGitHubApp';
import { getErrorMessage } from '../../../ReactQuery';
import { BUTTON_TYPE, LoaderButton } from '../../generic';
import Loader from '../../generic/Loader';
import Header from '../../shared/settings/SettingsPageHeader';
import Section from '../../shared/settings/SettingsPageSection';
import type { AppRole, VcsApp } from '../../../../shared/vcs/githubApp/apps';

const APP_LABELS: Record<VcsApp, string> = {
  fossabot: 'fossabot (write)',
  fossaCore: 'FOSSA Core (read)',
};

const VCS_APPS = Object.keys(VCS_APP_ROLE) as VcsApp[];

const InstalledState = ({
  app,
  accountName,
  accountType,
  appRole,
  isFetching,
}: {
  app: VcsApp;
  accountName?: string;
  accountType?: string;
  appRole?: AppRole | null;
  isFetching: boolean;
}) => {
  const { onUninstall, isUninstalling } = useUninstallGitHubApp();
  return (
    <>
      <h4>
        Installed on <b>{accountName ?? 'unknown account'}</b>
        {accountType ? <> ({accountType})</> : null}
        {appRole ? <> &middot; role: <b>{appRole}</b></> : null}
      </h4>
      <div className="margin-top-lg">
        <LoaderButton
          type={BUTTON_TYPE.SECONDARY}
          onClick={() => onUninstall(app)}
          isLoading={isUninstalling}
          isDisabled={isUninstalling || isFetching}
        >
          Disconnect
        </LoaderButton>
      </div>
    </>
  );
};

const NotInstalledState = ({ app, isFetching }: { app: VcsApp; isFetching: boolean }) => {
  const startInstall = useStartGitHubAppInstall();
  return (
    <>
      <h4>Not installed</h4>
      <div className="margin-top-lg">
        <LoaderButton
          type={BUTTON_TYPE.PRIMARY}
          onClick={() => startInstall.mutate(app)}
          isLoading={startInstall.isPending}
          isDisabled={startInstall.isPending || isFetching}
        >
          Install
        </LoaderButton>
      </div>
    </>
  );
};

const VcsAppRowBody = ({ app }: { app: VcsApp }) => {
  const { data, error, isLoading, isFetching, isError } = useGitHubAppInstallation(app);

  if (isLoading) {
    return <Loader />;
  }
  if (isError) {
    return <h4>{getErrorMessage(error, 'Failed to load installation status')}</h4>;
  }
  if (data?.installed) {
    return (
      <InstalledState
        app={app}
        accountName={data.accountName}
        accountType={data.accountType}
        appRole={data.appRole}
        isFetching={isFetching}
      />
    );
  }
  return <NotInstalledState app={app} isFetching={isFetching} />;
};

const VcsAppRow = ({ app }: { app: VcsApp }) => (
  <Section>
    <Section.Left heading={APP_LABELS[app]}>
      <Section.Paragraph>
        Exercises <code>{`/api/vcs/github-apps/${app}/*`}</code> end-to-end.
        The install button redirects to GitHub; the callback returns you here.
      </Section.Paragraph>
    </Section.Left>
    <Section.Right>
      <VcsAppRowBody app={app} />
    </Section.Right>
  </Section>
);

export const VcsGitHubAppDevPanel = () => {
  const addToast = useAddToast();
  const isEnabled = isFeatureEnabled(VCS_SERVICE_GITHUB_APP);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const status = params.get('vcs_install');
    if (!status) {
      return;
    }

    if (status === 'success') {
      addToast({ type: 'success', content: 'GitHub App installed' });
    }

    params.delete('vcs_install');
    const cleaned = `${window.location.pathname}${params.toString() ? `?${params}` : ''}`;
    window.history.replaceState({}, '', cleaned);
  }, [addToast, isEnabled]);

  if (!isEnabled) {
    return null;
  }

  return (
    <>
      <Header heading="vcs-service (dev)" />
      {VCS_APPS.map((app) => <VcsAppRow key={app} app={app} />)}
    </>
  );
};
